import type { LicenseType } from "@/data-access/user/meTenantLicenseTypes";
import type {
  MeFeedLastLocation,
  MeTenant,
  MeUser,
} from "@/data-access/user/queries/me-queries";
import {
  defaultUserInfo,
  type FeedLastLocation,
  type Tenant,
  type UserInfo,
} from "@/types/user/userInfo";

const parseId = (id: string): number => Number(id);

export const mapMeFeedLastLocationToFeedLastLocation = (
  entry: MeFeedLastLocation,
): FeedLastLocation => ({
  feed_id: entry.feedId,
  surface: entry.surface,
  location: entry.location,
  updated_at: entry.updatedAt,
});

const mapMeTenantToTenant = (tenant: MeTenant): Tenant => ({
  id: parseId(tenant.id),
  name: tenant.name,
  slug: tenant.slug,
  website: tenant.website,
  owner: tenant.owner ? parseId(tenant.owner.id) : null,
  logo: tenant.logo?.url ?? null,
  active_license_types: [...tenant.activeLicenseTypes] as LicenseType[],
});

/**
 * Maps a gql-tada `me` result to the REST-shaped {@link UserInfo} used across the app.
 * GQL `UserType` omits REST-only profile fields (`date_joined`, `walkthrough_completed`, etc.).
 */
export const mapMeToUserInfo = (me: MeUser): UserInfo => {
  const defaultTenant = me.defaultTenant
    ? mapMeTenantToTenant(me.defaultTenant)
    : defaultUserInfo.default_tenant;
  const [firstName, ...lastNameParts] = me.name.trim().split(/\s+/);

  return {
    id: parseId(me.id),
    name: me.name,
    email: me.email,
    username: me.email,
    first_name: firstName ?? "",
    last_name: lastNameParts.join(" "),
    avatar: me.avatar?.url ?? null,
    last_visited_location: me.lastVisitedLocation ?? null,
    feed_last_locations: (me.feedLastLocations ?? []).map(
      mapMeFeedLastLocationToFeedLastLocation,
    ),
    date_joined: "",
    default_tenant_id: defaultTenant.id,
    default_tenant: defaultTenant,
    tenants: me.tenants.map(mapMeTenantToTenant),
  };
};
